import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import Footer from './Footer';

const POLL_INTERVAL_MS = 4000;
const MAX_POLLS = 45; // ~3 minutes, then ask the donor to check back later

// Landing page after the Centiwise payment form. The redirect itself isn't
// trusted, so we poll our server (which asks the gateway) for the final status.
const DonationResult = () => {
  const [params] = useSearchParams();
  const orderId = params.get('order');
  const [donation, setDonation] = useState(null);
  const [error, setError] = useState('');
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!orderId) {
      setError('No donation reference was provided.');
      return undefined;
    }
    let cancelled = false;
    let timer;
    let polls = 0;

    const check = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BASE_URL}/api/donations/status/${encodeURIComponent(orderId)}`);
        if (!res.ok) throw new Error(res.status === 404 ? 'We could not find this donation.' : 'Could not check the payment status.');
        const data = await res.json();
        if (cancelled) return;
        setDonation(data);
        if (data.final) return;
      } catch (err) {
        if (!cancelled) setError(err.message);
        return;
      }
      polls += 1;
      if (polls >= MAX_POLLS) setTimedOut(true);
      else timer = setTimeout(check, POLL_INTERVAL_MS);
    };

    check();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [orderId]);

  const approved = donation?.status === 'approved';
  const failed = donation?.final && !approved;

  return (
    <div className="bg-white dark:bg-black transition-colors duration-300 min-h-screen">
      <div className="px-4 pt-[150px] pb-16 flex justify-center">
        <div className="bg-white dark:bg-[#111010] border border-gray-200 dark:border-white/10 shadow-sm text-gray-900 dark:text-white p-8 rounded-xl max-w-md w-full text-center font-montserrat">
          {error ? (
            <>
              <FaTimesCircle className="mx-auto text-red-500 mb-4" size={48} />
              <h2 className="text-2xl font-glsnecb mb-2">Something went wrong</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm">{error}</p>
            </>
          ) : approved ? (
            <>
              <FaCheckCircle className="mx-auto text-green-500 mb-4" size={48} />
              <h2 className="text-3xl font-glsnecb mb-2">Thank you{donation.firstName ? `, ${donation.firstName}` : ''}!</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">
                Your donation of <strong className="text-gray-900 dark:text-white">{donation.amount.toFixed(2)} {donation.currency}</strong> was received.
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-xs">Reference: {donation.orderId}</p>
            </>
          ) : failed ? (
            <>
              <FaTimesCircle className="mx-auto text-red-500 mb-4" size={48} />
              <h2 className="text-2xl font-glsnecb mb-2">Payment not completed</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">
                {donation.errorMessage || 'Your payment was not approved. No money was taken.'}
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-xs">Reference: {donation.orderId}</p>
            </>
          ) : (
            <>
              <FaSpinner className="mx-auto animate-spin text-gray-400 mb-4" size={40} />
              <h2 className="text-2xl font-glsnecb mb-2">Confirming your payment…</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                {timedOut
                  ? 'This is taking longer than usual. If your payment went through, it will be confirmed shortly. You can safely close this page.'
                  : 'Please keep this page open.'}
              </p>
            </>
          )}

          <Link
            to="/donation"
            className="inline-block mt-6 bg-black text-white dark:bg-white dark:text-black py-2 px-6 rounded-lg text-sm hover:opacity-90"
          >
            {failed || error ? 'Try again' : 'Back to Donation'}
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DonationResult;
